import get from 'lodash/get';
import type { SVGProps } from 'react';
import React, { useState } from 'react';
import type {
  GlobalError,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ArchivesAttachment } from '@/lib/service/type';
import { archivesMessages, Dragdrop } from '@/messages/archives';
import { formMessages } from '@/messages/forms';

import { Box } from '../Box';
import { Input } from '../Input';
import { InputErrorMessage } from '../InputErrorMessage';

import { ReactComponent as CameraSvg } from '@/assets/camera.svg';
import { ReactComponent as AddSvg } from '@/assets/icons/add.svg';

import * as S from './UploadInputField.styles';
export interface UploadInputField {
  icon?: React.ComponentType<SVGProps<SVGSVGElement>>;
  name: string;
  showInput?: boolean;
}

interface ImagePreviewProps {
  handleDeleteImage: () => void;
  name?: string;
  url: string;
}
interface InputFieldProps extends UploadInputField {
  clearErrors: UseFormClearErrors<any>;
  hasError?: boolean;
  name: string;
  onChange: (value: ArchivesAttachment[]) => void;
  setError: UseFormSetError<any>;
  showInput?: boolean;
  value: ArchivesAttachment[];
}
export const UploadImagePreview = ({
  handleDeleteImage,
  name,
  url,
}: ImagePreviewProps) => {
  return (
    <S.ImagePreview>
      <S.PreviewImageStyle alt={name} height='108' src={url} width='108' />
      <S.CloseIconButton onClick={() => handleDeleteImage()} />
    </S.ImagePreview>
  );
};
const InputField = ({
  clearErrors,
  hasError = false,
  name,
  onChange,
  setError,
  showInput = false,
  value,
}: InputFieldProps) => {
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const { formatMessage } = useIntl();
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file); // Convert to base64
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  const handleFileSelect = async (
    files: FileList | null,
    value: ArchivesAttachment[],
    onChange: (value: ArchivesAttachment[]) => void,
    inputRef: HTMLInputElement | null,
  ) => {
    if (!files) return;
    clearErrors();
    const acceptedFormats = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];
    const selectedFiles = Array.from(files);
    const allFilesLength = value.length + selectedFiles.length;
    // Filter valid files
    if (selectedFiles.length > 2 || allFilesLength > 2) {
      return setError(name, {
        type: 'length',
        message: formatMessage(formMessages.archivesInvalidUploadLimit),
      });
    }
    const validFiles = selectedFiles.filter((file) => {
      const isValidFormat = acceptedFormats?.includes(file.type);
      const isValidSize = file.size <= 2 * 1024 * 1024; // 2MB in bytes
      if (!isValidFormat) {
        return setError(name, {
          type: 'type',
          message: `${file.name} ${formatMessage(
            formMessages.archivesInvalidUploadTypeLimit,
          )}`,
        });
      }
      if (!isValidSize) {
        return setError(name, {
          type: 'size',
          message: `${file.name} ${formatMessage(
            formMessages.archivesInvalidUploadSizeLimit,
          )}`,
        });
      }
      return isValidFormat && isValidSize;
    });

    const base64Files = await Promise.all(
      validFiles.map(async (file) => {
        const fileBase64 = await fileToBase64(file);
        return {
          base64: fileBase64 ?? '',
          file_name: file.name,
          file_type: file.type,
        };
      }),
    );

    // Append the new files to the existing files
    const newFiles = [...value, ...base64Files];
    const newUrls = validFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls((prevUrls) => [...prevUrls, ...newUrls]);
    onChange(newFiles); // Update form value with the new array of files
    if (inputRef) {
      inputRef.value = ''; // Reset file input
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (
    e: React.DragEvent<HTMLElement>,
    value: ArchivesAttachment[],
    onChange: (value: ArchivesAttachment[]) => void,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    handleFileSelect(files, value, onChange, e.target as HTMLInputElement);
  };

  const handleDeleteImage = (
    index: number,
    value: ArchivesAttachment[],
    onChange: (value: ArchivesAttachment[]) => void,
  ) => {
    const updatedUrls = previewUrls.filter((_, i) => i !== index);
    const updatedFiles = value.filter((_, i) => i !== index);
    setPreviewUrls(updatedUrls);
    onChange(updatedFiles);
  };
  const handleUpload = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    document.getElementById('fileInput')?.click();
  };

  return (
    <S.DropzoneStyle $error={hasError} data-testid='InputField'>
      <Input
        accept='image/*'
        id='fileInput'
        multiple
        onChange={(e) =>
          handleFileSelect(e.target.files, value, onChange, e.target)
        }
        style={{ display: showInput ? 'block' : 'none' }}
        type='file'
      />
      {value?.length > 0 ? (
        <S.PreviewContainerStyle>
          {value?.map((item: ArchivesAttachment, index: number) => (
            <UploadImagePreview
              key={item.file_name}
              handleDeleteImage={() =>
                handleDeleteImage(index, value, onChange)
              }
              name={item.file_name}
              url={item.base64}
            />
          ))}
          {value?.length < 2 && (
            <S.AddImageButton
              onClick={() => document.getElementById('fileInput')?.click()}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, value, onChange)}
            >
              <AddSvg />
            </S.AddImageButton>
          )}
        </S.PreviewContainerStyle>
      ) : (
        <S.DropzoneInfoWrapper
          onClick={(e) => handleUpload(e)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, value, onChange)}
        >
          <CameraSvg />
          <S.Text>
            <Dragdrop />
          </S.Text>
        </S.DropzoneInfoWrapper>
      )}
    </S.DropzoneStyle>
  );
};
export const UploadInputField = ({ ...props }: UploadInputField) => {
  const name = props.name;
  const { clearErrors, control, formState, register, setError } =
    useFormContext();
  const { formatMessage } = useIntl();

  const error = get(formState.errors, name) as GlobalError;

  return (
    <Box data-testid='UploadInputFieldWrapper'>
      <S.Prompt>
        {formatMessage(archivesMessages.dragdropImagePrompt)}*
      </S.Prompt>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <InputField
            {...props}
            clearErrors={clearErrors}
            hasError={!!error}
            setError={setError}
            {...register(name)}
            onChange={onChange}
            value={value}
          ></InputField>
        )}
      />
      <InputErrorMessage data-test-id='SelectFieldError' error={error} />
    </Box>
  );
};
UploadInputField.UploadImagePreview = UploadImagePreview;
UploadInputField.InputField = InputField;

UploadInputField.displayName = 'UploadInputField';
